/**
 * 🛡️ G2GENTE - Mobile Notifications Page
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft,
  Loader2,
  Bell,
  BellOff,
  Briefcase,
  UserPlus,
  MessageSquare,
  Check,
  Trash2,
  CheckCheck,
  Brain,
  CalendarClock
} from 'lucide-react';
import { toast } from 'sonner';

export default function MobileNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, status: 'READ' } : n
      ));
    } catch (error) {
      toast.error('Erro ao marcar como lida');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, status: 'READ' })));
      toast.success('Todas marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar todas como lidas');
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsAPI.delete(id);
      setNotifications(notifications.filter(n => n.id !== id));
      toast.success('Notificação removida');
    } catch (error) {
      toast.error('Erro ao remover notificação');
    }
  };

  // Handle click on notification
  const handleClick = (notification) => {
    const notifType = notification.type || notification.tipo;
    const isUnread = notification.status === 'UNREAD' || notification.read === false;
    
    if (isUnread) {
      handleMarkAsRead(notification.id);
    }
    
    // Tratar notificação de Big Five
    if (notifType === 'BIGFIVE_REQUIRED' && notification.data) {
      const { job_id, bigfive_version, company_name } = notification.data;
      const url = `/m/bigfive?oficial=true&versao=${bigfive_version}&job_id=${job_id}&empresa=${encodeURIComponent(company_name)}`;
      navigate(url);
      return;
    }
    
    if (notification.url) {
      navigate(notification.url);
    }
  };

  const getIcon = (tipo) => {
    const icons = {
      SOLICITATION: UserPlus,
      NEW_JOB: Briefcase,
      CHAT_MESSAGE: MessageSquare,
      APPLICATION_STATUS: Check,
      BIGFIVE_REQUIRED: Brain,
      INTERVIEW: CalendarClock,
    };
    return icons[tipo] || Bell;
  };

  const getIconColor = (tipo) => {
    const colors = {
      SOLICITATION: 'bg-purple-100 text-purple-600',
      NEW_JOB: 'bg-cyan-100 text-cyan-600',
      CHAT_MESSAGE: 'bg-emerald-100 text-emerald-600',
      APPLICATION_STATUS: 'bg-amber-100 text-amber-600',
      BIGFIVE_REQUIRED: 'bg-violet-100 text-violet-600',
      INTERVIEW: 'bg-cyan-100 text-cyan-600',
    };
    return colors[tipo] || 'bg-slate-100 text-slate-600';
  };

  const unreadCount = notifications.filter(n => n.status === 'UNREAD').length;

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="mobile-notifications">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/m')} className="text-slate-600">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold text-slate-900">Notificações</h1>
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-cyan-600"
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Ler todas
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-20">
            <BellOff className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">Sem notificações</h3>
            <p className="text-sm text-slate-600">
              Você receberá alertas sobre vagas e solicitações aqui.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => {
              // Support both field formats (pt and en)
              const notifType = notification.type || notification.tipo;
              const notifTitle = notification.title || notification.titulo;
              const notifMessage = notification.message || notification.mensagem;
              const isUnread = notification.status === 'UNREAD' || notification.read === false;
              
              const Icon = getIcon(notifType);
              const iconColor = getIconColor(notifType);
              
              return (
                <Card 
                  key={notification.id}
                  className={`border-slate-200 cursor-pointer transition-all active:scale-[0.99] ${
                    isUnread ? 'bg-cyan-50/50 border-cyan-200' : ''
                  }`}
                  onClick={() => handleClick(notification)}
                  data-testid={`notification-${notification.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconColor}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className={`text-sm ${isUnread ? 'font-semibold' : 'font-medium'} text-slate-900`}>
                            {notifTitle}
                          </h3>
                          {isUnread && (
                            <span className="w-2 h-2 bg-cyan-500 rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2">
                          {notifMessage}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(notification.created_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notification.id);
                        }}
                        className="p-1 text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
